import styled from 'styled-components';
import { Layout } from 'antd';

import {
  FeaturedSection,
  Hero,
  LatestSection,
  StatsSection,
  RenewalServiceSection,
  PartnerCarousel,
  MissionSection,
  EncouragementSection,
  CommitteeSection,
  SectionHeading,
} from '../component/landing';
import type { ServiceHighlight } from '../component/landing/RenewalServiceSection';
import useReveal, { revealStyle } from '../component/landing/useReveal';
import { NoticePopup } from '../component/common';
import { useMessages } from '../i18n';

const { Content } = Layout;

interface ServiceIntro {
  label: string;
  title: string;
  subtitle: string;
  description: string;
  highlights: ServiceHighlight[];
  image: string;
  imageAlt: string;
}

const servicesKo: ServiceIntro[] = [
  {
    label: 'Digital Slide Viewer',
    title: 'High-Resolution Whole Slide Image Viewing',
    subtitle: '설치 없이 브라우저에서 바로 여는 기가픽셀 디지털 슬라이드',
    description:
      '스캐너로 얻은 수 기가픽셀 크기의 전체 슬라이드 이미지(WSI)를 Deep Zoom 타일 피라미드로 변환해 제공합니다. ' +
      '화면에 필요한 해상도의 타일만 불러오기 때문에, 저배율 전체 조망부터 고배율 세포 관찰까지 현미경을 다루듯 끊김 없이 확대하고 이동할 수 있습니다.',
    highlights: [
      { title: '실측 기반 스케일', desc: '슬라이드의 MPP(microns per pixel) 정보를 읽어 스케일바와 배율을 실제 크기에 맞게 표시합니다.' },
      { title: '색 재현과 영상 보정', desc: 'ICC 색 프로파일, 감마 보정, 회전으로 스캐너 고유의 염색 색감을 정확하게 살펴봅니다.' },
      { title: '내비게이터와 캡처', desc: '미니맵으로 현재 시야의 위치를 확인하고, 필요한 시야를 이미지로 바로 저장합니다.' },
      { title: '안정적인 대용량 업로드', desc: '중간에 끊겨도 이어 올리는 TUS 업로드와 타일 무결성 검사(QC)로 슬라이드 품질을 관리합니다.' },
    ],
    image: '/img/main/main_viewer.PNG',
    imageAlt: 'Digital slide viewer',
  },
  {
    label: 'Atlas Gallery',
    title: 'Organ-Specific Disease Gallery',
    subtitle: '장기계통별로 정리된 소아 희귀질환 증례 아틀라스',
    description:
      '등록된 증례를 장기계통과 질환 분류에 따라 체계적으로 정리해, 찾고 싶은 질환에 몇 번의 클릭으로 닿을 수 있습니다. ' +
      '각 증례는 디지털 슬라이드와 함께 임상 정보, 현미경 사진, 육안 사진, 참고 자료를 하나의 화면에 묶어 보여줍니다.',
    highlights: [
      { title: '분류 체계 기반 탐색', desc: 'WHO 분류처럼 계층적으로 구성된 질환 포털과 장기계통별 그리드 뷰로 증례를 찾아갑니다.' },
      { title: '증례 단위 통합 열람', desc: '슬라이드, 현미경 사진, 육안(Gross) 사진, 첨부 자료를 묶어 함께 제공합니다.' },
      { title: '구조화된 증례 정보', desc: '장기와 질환을 기준으로 증례별 정보를 일관된 형식으로 정리합니다.' },
      { title: '빠른 증례 검색', desc: '키워드 검색으로 질환명과 소견에 맞는 증례를 빠르게 찾을 수 있습니다.' },
    ],
    image: '/img/main/main_gallery.PNG',
    imageAlt: 'Atlas gallery',
  },
  {
    label: 'Slide Annotation',
    title: 'Interactive Annotation and Labeling Tools',
    subtitle: '진단 소견을 슬라이드 위에 직접 기록하고 공유합니다',
    description:
      '화살표, 선, 사각형, 타원, 자유곡선, 텍스트 도구로 핵심 형태학적 소견을 정확한 위치에 표시합니다. ' +
      '주석은 슬라이드 좌표 기준으로 저장되어 어떤 배율에서 보더라도 같은 자리에 겹쳐 보이며, 증례를 보는 모든 사람이 같은 소견을 함께 읽을 수 있습니다.',
    highlights: [
      { title: '다양한 그리기 도구', desc: '화살표·선·사각형·타원·자유곡선과 텍스트 라벨로 병변을 표시합니다.' },
      { title: '실측 길이 측정', desc: '눈금자 도구가 MPP를 기준으로 병변의 크기를 mm 단위로 계산합니다.' },
      { title: '주석 목록', desc: '주석 목록을 통해 슬라이드에 기록된 모든 주석을 한 번에 확인할 수 있습니다' },
      { title: '사진 주석과 코멘트', desc: '현미경·육안 사진에도 주석을 남기고, 슬라이드별 코멘트로 의견을 나눕니다.' },
    ],
    image: '/img/main/main_annotation.PNG',
    imageAlt: 'Slide annotation tools',
  },
];

const servicesEn: ServiceIntro[] = [
  {
    label: 'Digital Slide Viewer',
    title: 'High-Resolution Whole Slide Image Viewing',
    subtitle: 'Gigapixel digital slides that open right in your browser, with nothing to install',
    description:
      'Whole slide images (WSI) of several gigapixels captured by slide scanners are converted into Deep Zoom tile pyramids. ' +
      'Only the tiles needed for the current view are loaded, so you can zoom and pan seamlessly from a low-power overview to high-power cellular detail, just as you would at the microscope.',
    highlights: [
      { title: 'True-to-Scale Display', desc: "Reads each slide's MPP (microns per pixel) to show the scale bar and magnification at true physical size." },
      { title: 'Color Fidelity and Image Adjustment', desc: "ICC color profiles, gamma correction and rotation let you examine each scanner's staining colors accurately." },
      { title: 'Navigator and Capture', desc: 'A minimap shows where the current field of view is, and any field can be saved as an image right away.' },
      { title: 'Reliable Large-File Upload', desc: 'Resumable TUS uploads that pick up after interruptions, together with tile integrity checks (QC), keep slide quality under control.' },
    ],
    image: '/img/main/main_viewer.PNG',
    imageAlt: 'Digital slide viewer',
  },
  {
    label: 'Atlas Gallery',
    title: 'Organ-Specific Disease Gallery',
    subtitle: 'An atlas of rare pediatric disease cases organized by organ system',
    description:
      'Registered cases are organized by organ system and disease classification, so you can reach the disease you are looking for in a few clicks. ' +
      'Each case brings together digital slides, clinical information, microscopic photos, gross photos and references on a single screen.',
    highlights: [
      { title: 'Classification-Based Browsing', desc: 'Find cases through a hierarchical disease portal modeled on the WHO classification and a grid view by organ system.' },
      { title: 'Integrated Case View', desc: 'Slides, microscopic photos, gross photos and attachments are presented together.' },
      { title: 'Structured Case Information', desc: 'Information for each case is organized in a consistent format by organ and disease.' },
      { title: 'Fast Case Search', desc: 'Keyword search quickly finds cases that match disease names and findings.' },
    ],
    image: '/img/main/main_gallery.PNG',
    imageAlt: 'Atlas gallery',
  },
  {
    label: 'Slide Annotation',
    title: 'Interactive Annotation and Labeling Tools',
    subtitle: 'Record and share diagnostic findings directly on the slide',
    description:
      'Mark key morphological findings at their exact location with arrow, line, rectangle, ellipse, freehand and text tools. ' +
      'Annotations are stored in slide coordinates, so they stay in place at any magnification and everyone viewing the case reads the same findings.',
    highlights: [
      { title: 'Versatile Drawing Tools', desc: 'Mark lesions with arrows, lines, rectangles, ellipses, freehand curves and text labels.' },
      { title: 'True-Scale Measurement', desc: 'The ruler tool calculates lesion size in millimeters based on the slide MPP.' },
      { title: 'Annotation List', desc: 'See every annotation recorded on a slide at a glance in the annotation list.' },
      { title: 'Photo Annotations and Comments', desc: 'Annotate microscopic and gross photos too, and share opinions in per-slide comments.' },
    ],
    image: '/img/main/main_annotation.PNG',
    imageAlt: 'Slide annotation tools',
  },
];

const ko = {
  services: servicesKo,
  notice: (
    <>
      현재 사이트 구축 중으로 완성된 사이트가 아닙니다.
      <br />
      현재 manuscript 작성 및 검증, slide annotation 진행중입니다.
    </>
  ),
  platformTitle: <>진단 · 연구 · 교육을 위한<br />디지털 병리 플랫폼</>,
  platformDesc: '슬라이드 열람부터 증례 탐색, 소견 기록까지. 현미경 앞에서 하던 일을 웹 브라우저 하나로 이어갑니다.',
  casesTitle: '아틀라스에 담긴 증례를 만나보세요',
  casesDesc: '전문가들이 선정한 주목할 증례와 가장 최근에 등록된 증례입니다.',
};

const en: typeof ko = {
  services: servicesEn,
  notice: (
    <>
      This site is still under construction and is not yet complete.
      <br />
      Manuscript writing and validation, and slide annotation are currently in progress.
    </>
  ),
  platformTitle: <>A digital pathology platform<br />for diagnosis, research and education</>,
  platformDesc: 'From viewing slides to exploring cases and recording findings, the work you once did at the microscope now continues in a single web browser.',
  casesTitle: 'Explore the cases in the Atlas',
  casesDesc: 'Featured cases selected by experts, and the most recently added cases.',
};

// 랜딩 페이지 (리뉴얼 디자인, "/" 경로). 이전 디자인은 Landing.tsx
// 섹션 순서: 첫 화면 → 지표 → 미션·비전·핵심가치 → 플랫폼 기능 → 증례 → 격려의 글 → 운영진·자문위원 → 협력 기관
const LandingRenewal = () => {
  const platformRef = useReveal<HTMLElement>();
  const casesRef = useReveal<HTMLElement>();
  const t = useMessages({ ko, en });

  return (
    <StyledLayout>
      <NoticePopup>{t.notice}</NoticePopup>

      <Content>
        <Hero />
        <StatsSection />

        <MissionSection />

        <PlatformIntro ref={platformRef} id="platform">
          <div className="container">
            <SectionHeading
              eyebrow="Platform"
              title={t.platformTitle}
              description={t.platformDesc}
            />
          </div>
        </PlatformIntro>

        {t.services.map((service, index) => (
          <RenewalServiceSection
            key={service.label}
            index={String(index + 1).padStart(2, '0')}
            label={service.label}
            title={service.title}
            subtitle={service.subtitle}
            description={service.description}
            highlights={service.highlights}
            image={service.image}
            imageAlt={service.imageAlt}
            imagePosition={index % 2 === 0 ? 'left' : 'right'}
            background={index % 2 === 0 ? '#fff' : '#f6f8fb'}
          />
        ))}

        <CasesSection ref={casesRef} id="cases">
          <div className="container">
            <SectionHeading
              eyebrow="Case Library"
              title={t.casesTitle}
              description={t.casesDesc}
            />
            <FeaturedSection />
            <LatestSection />
          </div>
        </CasesSection>

        <EncouragementSection />
        <CommitteeSection />

        <PartnerCarousel />
      </Content>
    </StyledLayout>
  );
};

const StyledLayout = styled(Layout)`
  background: #fff;
`;

const PlatformIntro = styled.section`
  ${revealStyle}
  padding: 120px 0 0;
  background: #fff;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  > .container > div {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    padding: 80px 0 0;
  }
`;

const CasesSection = styled.section`
  ${revealStyle}
  padding: 120px 0 40px;
  background: #f6f8fb;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  @media (max-width: 768px) {
    padding: 80px 0 20px;
  }
`;

export default LandingRenewal;
